import React, { useState, useEffect } from 'react';
import { useAuth } from './App';
import ReactMarkdown from 'react-markdown';

const DharmaSastramPage = () => {
  const { language } = useAuth();
  const [activeTab, setActiveTab] = useState('reader'); // 'reader' or 'pdfs'
  const [qaData, setQaData] = useState([]);
  const [selectedPart, setSelectedPart] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [selection, setSelection] = useState({ text: '', translatedText: '', loading: false, x: 0, y: 0, show: false });
  
  const itemsPerPage = 1;

  useEffect(() => {
    const handleMouseUp = (e) => {
      // Hide the popup if clicking outside of it (unless clicking the popup itself)
      if (e.target.closest('#translate-popup')) return;

      // Small timeout to allow the browser to complete the selection
      setTimeout(() => {
        const activeSelection = window.getSelection();
        if (!activeSelection || activeSelection.rangeCount === 0) {
          setSelection(prev => ({ ...prev, show: false }));
          return;
        }
        
        const text = activeSelection.toString().trim();
        
        if (text.length > 0) {
          // Get the bounding rectangle of the selection to position our popup
          const range = activeSelection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          
          setSelection({
            text: text,
            translatedText: '',
            loading: true,
            x: rect.left + (rect.width / 2),
            y: rect.top + window.scrollY - 10,
            show: true
          });

          // Fetch translation directly to avoid opening a new tab
          fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=ta&tl=en&dt=t&q=${encodeURIComponent(text)}`)
            .then(res => res.json())
            .then(data => {
              if (data && data[0] && data[0][0] && data[0][0][0]) {
                setSelection(prev => ({ ...prev, translatedText: data[0][0][0], loading: false }));
              } else {
                setSelection(prev => ({ ...prev, translatedText: 'Translation unavailable.', loading: false }));
              }
            })
            .catch(err => {
              setSelection(prev => ({ ...prev, translatedText: 'Error fetching translation.', loading: false }));
            });

          // Clear selection to prevent third-party translation extensions from popping up
          setTimeout(() => {
            if (window.getSelection) {
              window.getSelection().removeAllRanges();
            }
          }, 50);

        } else {
          setSelection(prev => ({ ...prev, show: false }));
        }
      }, 10);
    };

    const handleMouseDown = (e) => {
      if (e.target.closest('#translate-popup')) return;
      setSelection(prev => ({ ...prev, show: false }));
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);
    
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  useEffect(() => {
    fetch('/dharma_sastram_shradhdha_kaandam.json')
      .then(res => res.json())
      .then(data => {
        setQaData(data || []);
      })
      .catch(err => console.error("Error loading Dharma Sastram data:", err));
  }, []);

  const pdfs = [
    {
      id: 1,
      title_en: 'Dharma Sastra Surukkam',
      title_ta: 'தர்ம சாஸ்திர சுருக்கம்',
      description_en: 'A concise summary of basic Dharma Sastras every Sri Vaishnava should know.',
      description_ta: 'ஒவ்வொரு ஸ்ரீ வைஷ்ணவனும் தெரிந்து கொள்ள வேண்டிய அடிப்படை தர்ம சாஸ்திரங்களின் சுருக்கம்.',
      filename: 'dharma-sastra-surukkam.pdf',
      icon: '📖'
    },
    {
      id: 2,
      title_en: 'Asoucha Kandam',
      title_ta: 'ஆசௌச காண்டம்',
      description_en: 'Rules and regulations regarding purity and impurity (Theetu) during births and deaths.',
      description_ta: 'பிறப்பு மற்றும் இறப்பின் போது ஏற்படும் தீட்டு மற்றும் தூய்மை குறித்த விதிமுறைகள்.',
      filename: 'asoucha-kandam.pdf',
      icon: '🕊️'
    },
    {
      id: 3,
      title_en: 'Dharma Sastram Volume 1',
      title_ta: 'தர்ம சாஸ்திரம் தொகுதி 1',
      description_en: 'Detailed volume on daily rituals and practices.',
      description_ta: 'அன்றாட கர்மாக்கள் மற்றும் நடைமுறைகள் குறித்த விரிவான தொகுதி.',
      filename: 'DS1.pdf',
      icon: '📚'
    },
    {
      id: 4,
      title_en: 'Shraddha Eligible Things',
      title_ta: 'ச்ராத்தத்திற்கு உகந்த பொருட்கள்',
      description_en: 'List of items permitted and prohibited during Shradhdham rituals.',
      description_ta: 'ச்ராத்த சடங்குகளின் போது அனுமதிக்கப்பட்ட மற்றும் தவிர்க்க வேண்டிய பொருட்களின் பட்டியல்.',
      filename: 'shraaddha-eligible-things.pdf',
      icon: '🌾'
    },
    {
      id: 5,
      title_en: 'Shradham Importants',
      title_ta: 'ச்ராத்தத்தின் முக்கிய குறிப்புகள்',
      description_en: 'Key rules and procedures for performing annual ceremonies.',
      description_ta: 'வருடாந்திர சடங்குகளைச் செய்வதற்கான முக்கிய விதிகள் மற்றும் நடைமுறைகள்.',
      filename: 'shradham-importants.pdf',
      icon: '🕉️'
    }
  ];

  // Extract unique parts
  const uniqueParts = Array.from(new Set(qaData.map(item => item.part))).filter(Boolean);

  // Filtered pages
  const filteredData = qaData.filter(item => {
    const matchesPart = selectedPart ? item.part === selectedPart : true;
    const matchesSearch = searchTerm 
      ? (item.sloka_devanagari_1a || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (item.meaning_tamil_1b || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.text || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.image || '').toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    return matchesPart && matchesSearch;
  });

  const totalPagesCount = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  const handlePageSubmit = (e) => {
    e.preventDefault();
    const pageNum = parseInt(pageInput);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPagesCount) {
      setCurrentPage(pageNum);
      window.scrollTo({ top: 200, behavior: 'smooth' });
    } else {
      setPageInput(currentPage.toString());
    }
  };

  const PaginationControls = () => {
    if (totalPagesCount <= 1) return null;
    return (
      <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4 py-4 bg-orange-50/80 rounded-xl border border-orange-200 shadow-sm my-6">
        <button
          onClick={() => { setCurrentPage(1); window.scrollTo({ top: 200, behavior: 'smooth' }); }}
          disabled={currentPage === 1}
          className="px-3 py-2 md:px-4 md:py-2 rounded-lg font-bold bg-white border border-orange-300 text-orange-800 disabled:opacity-40 hover:bg-orange-100 shadow-sm transition-colors text-sm"
        >
          {language === 'en' ? '≪ First' : '≪ முதல்'}
        </button>
        <button
          onClick={() => { setCurrentPage(prev => Math.max(prev - 1, 1)); window.scrollTo({ top: 200, behavior: 'smooth' }); }}
          disabled={currentPage === 1}
          className="px-3 py-2 md:px-4 md:py-2 rounded-lg font-bold bg-white border border-orange-300 text-orange-800 disabled:opacity-40 hover:bg-orange-100 shadow-sm transition-colors text-sm"
        >
          {language === 'en' ? '< Prev' : '< முந்தைய'}
        </button>
        
        <form onSubmit={handlePageSubmit} className="flex items-center gap-2 mx-2">
          <span className="text-gray-600 text-sm font-semibold hidden sm:inline">{language === 'en' ? 'Page' : 'பக்கம்'}</span>
          <input 
            type="number"
            min="1"
            max={totalPagesCount}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={handlePageSubmit}
            className="w-16 p-2 text-center border-2 border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none font-bold text-orange-900"
          />
          <span className="text-gray-600 text-sm font-semibold">/ {totalPagesCount}</span>
        </form>

        <button
          onClick={() => { setCurrentPage(prev => Math.min(prev + 1, totalPagesCount)); window.scrollTo({ top: 200, behavior: 'smooth' }); }}
          disabled={currentPage === totalPagesCount}
          className="px-3 py-2 md:px-4 md:py-2 rounded-lg font-bold bg-white border border-orange-300 text-orange-800 disabled:opacity-40 hover:bg-orange-100 shadow-sm transition-colors text-sm"
        >
          {language === 'en' ? 'Next >' : 'அடுத்த >'}
        </button>
        <button
          onClick={() => { setCurrentPage(totalPagesCount); window.scrollTo({ top: 200, behavior: 'smooth' }); }}
          disabled={currentPage === totalPagesCount}
          className="px-3 py-2 md:px-4 md:py-2 rounded-lg font-bold bg-white border border-orange-300 text-orange-800 disabled:opacity-40 hover:bg-orange-100 shadow-sm transition-colors text-sm"
        >
          {language === 'en' ? 'Last ≫' : 'கடைசி ≫'}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-amber-50 to-white py-10 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header Hero Banner */}
        <div className="text-center mb-10">
          <div className="inline-block bg-orange-100 text-orange-800 text-xs font-semibold px-4 py-1.5 rounded-full mb-3 uppercase tracking-widest border border-orange-200 shadow-sm">
            {language === 'en' ? 'Authentic Vaideeka Scriptures' : 'ஆதென்டிக் வைதீக தர்ம சாஸ்திரம்'}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-orange-900 mb-4 font-serif">
            {language === 'en' ? 'Dharma Sastram — Shradhdha Kaandam' : 'தர்ம சாஸ்திரம் — ஶ்ராத்த காண்டம்'}
          </h1>
          <p className="text-base md:text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {language === 'en' 
              ? 'Complete 701 Pages of ancient Vaideeka Dharma Sastram with authentic Tamil script.' 
              : 'ஸ்ரீ வைஷ்ணவ தர்ம சாஸ்திர ஶ்ராத்த காண்டத்தின் 701 பக்கங்களின் தமிழ் உரை தொகுப்பு.'}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8 border-b border-orange-200">
          <button
            onClick={() => { setActiveTab('reader'); setCurrentPage(1); }}
            className={`py-3 px-6 font-bold text-base md:text-lg transition-all border-b-4 ${
              activeTab === 'reader'
                ? 'border-orange-600 text-orange-800 bg-orange-100/50 rounded-t-lg'
                : 'border-transparent text-gray-600 hover:text-orange-700'
            }`}
          >
            📖 {language === 'en' ? 'Interactive Reader (701 Pages)' : 'ஶ்ராத்த காண்டம் வாசிப்பான் (701 பக்கங்கள்)'}
          </button>
          <button
            onClick={() => setActiveTab('pdfs')}
            className={`py-3 px-6 font-bold text-base md:text-lg transition-all border-b-4 ${
              activeTab === 'pdfs'
                ? 'border-orange-600 text-orange-800 bg-orange-100/50 rounded-t-lg'
                : 'border-transparent text-gray-600 hover:text-orange-700'
            }`}
          >
            📥 {language === 'en' ? 'PDF Downloads' : 'PDF பதிவிறக்கங்கள்'}
          </button>
        </div>

        {/* Tab 1: Interactive Reader */}
        {activeTab === 'reader' && (
          <div>
            {/* Filter and Search Bar */}
            <div className="bg-white p-6 rounded-2xl shadow-md border border-orange-200 mb-4 flex flex-col md:flex-row gap-4 justify-between items-center">
              
              {/* Part Filter */}
              <div className="w-full md:w-1/2">
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                  {language === 'en' ? 'Select Part' : 'பாகத்தைத் தேர்ந்தெடுக்கவும்'}
                </label>
                <select
                  value={selectedPart}
                  onChange={(e) => { setSelectedPart(e.target.value); setCurrentPage(1); }}
                  className="w-full p-3 rounded-xl border border-orange-300 bg-orange-50/50 text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">{language === 'en' ? 'All Parts (701 Pages)' : 'அனைத்து பாகங்களும் (701 பக்கங்கள்)'}</option>
                  {uniqueParts.map((p, idx) => (
                    <option key={p} value={p}>
                      {`Part ${idx + 1} (${p.split('_').pop() || p})`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search Bar */}
              <div className="w-full md:w-1/2">
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                  {language === 'en' ? 'Search in Scripture' : 'நூலில் தேடவும்'}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    placeholder={language === 'en' ? 'Search Tamil word...' : 'தேடல் சொல்லை உள்ளிடவும் (எ.கா: ச்ராத்தம், ஸ்நானம்)...'}
                    className="w-full p-3 pl-10 rounded-xl border border-orange-300 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="absolute left-3 top-3.5 text-gray-400 text-lg">🔍</span>
                </div>
              </div>
            </div>
            
            {/* Top Pagination */}
            <PaginationControls />

            {/* Content Display */}
            {paginatedData.length > 0 ? (
              <div className="mb-4">
                {paginatedData.map((item, idx) => {
                  const itemKey = item.page_num || (startIndex + idx + 1);
                  
                  // Use meaning_tamil_1b if it exists and has content, otherwise fall back to text, otherwise sloka
                  const content = item.meaning_tamil_1b?.trim() 
                                    ? item.meaning_tamil_1b 
                                    : (item.text?.trim() ? item.text : item.sloka_devanagari_1a);

                  return (
                    <div key={idx} className="bg-white rounded-2xl shadow-xl border border-orange-200 overflow-hidden hover:shadow-2xl transition-all">
                      
                      {/* Card Top Banner */}
                      <div className="bg-gradient-to-r from-orange-900 to-amber-800 text-amber-50 px-6 py-3.5 font-bold flex justify-between items-center shadow-inner">
                        <div className="flex items-center space-x-3">
                          <span className="bg-amber-500 text-orange-950 text-xs px-2.5 py-1 rounded-md font-mono font-black">
                            PAGE {item.page_num || (startIndex + idx + 1)}
                          </span>
                          <span className="text-sm md:text-base font-serif tracking-wide">
                            📖 {item.image?.replace('.jpg', '') || `Page ${itemKey}`}
                          </span>
                        </div>
                        <span className="text-xs bg-orange-950/60 text-orange-200 px-3 py-1 rounded-full font-mono border border-orange-700">
                          {item.part?.split('_Pages_')[0]?.replace('dharma-sastram-shradhdha-kaandam_', '') || item.part}
                        </span>
                      </div>

                      {/* Content Body using the user-preferred styling (prose-amber) */}
                      <div className="p-6 md:p-10 bg-white">
                        <div className="prose prose-amber max-w-none text-gray-800 text-base md:text-lg leading-relaxed text-justify whitespace-pre-line bg-amber-50/40 p-6 md:p-10 rounded-xl border border-amber-100 shadow-inner min-h-[400px]">
                          <ReactMarkdown>{content || ''}</ReactMarkdown>
                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-12 text-center border border-orange-200 text-gray-600 mb-10">
                <p className="text-xl font-bold text-orange-800 mb-2">No pages found matching your search.</p>
                <p className="text-sm">Try clearing the search filter or selecting a different Part.</p>
              </div>
            )}

            {/* Bottom Pagination */}
            <PaginationControls />
          </div>
        )}

        {/* Tab 2: PDF Downloads */}
        {activeTab === 'pdfs' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pdfs.map((pdf) => (
              <div key={pdf.id} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-t-4 border-orange-600 group flex flex-col">
                <div className="p-8 flex-grow">
                  <div className="text-5xl mb-6 text-center transform group-hover:scale-110 transition-transform duration-300">
                    {pdf.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                    {language === 'en' ? pdf.title_en : pdf.title_ta}
                  </h3>
                  <p className="text-gray-600 text-center leading-relaxed">
                    {language === 'en' ? pdf.description_en : pdf.description_ta}
                  </p>
                </div>
                <div className="p-6 bg-orange-50 mt-auto border-t border-orange-100">
                  <a 
                    href={`/dharma-sastram-pdfs/${pdf.filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-full bg-orange-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-orange-700 transition-colors shadow-md"
                  >
                    <span className="mr-2">📥</span>
                    {language === 'en' ? 'Download PDF' : 'பதிவிறக்கம் செய்க'}
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Custom Google Translate Popup */}
      {selection.show && (
        <div 
          id="translate-popup"
          className="absolute z-50 transform -translate-x-1/2 -translate-y-full pb-2 w-max max-w-xs md:max-w-md"
          style={{ left: `${selection.x}px`, top: `${selection.y}px` }}
        >
          <div className="bg-gray-900 text-white p-3 rounded-lg shadow-xl border border-gray-700">
            <div className="flex justify-between items-start mb-2 gap-4">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
                <span>🌐</span> Google Translate
              </div>
              <button onClick={() => setSelection(prev => ({ ...prev, show: false }))} className="text-gray-400 hover:text-white transition-colors">
                ✕
              </button>
            </div>
            <div className="text-sm font-medium">
              {selection.loading ? (
                <span className="animate-pulse">{language === 'en' ? 'Translating...' : 'மொழிபெயர்க்கப்படுகிறது...'}</span>
              ) : (
                <span className="text-amber-300">{selection.translatedText}</span>
              )}
            </div>
          </div>
          {/* Small triangle pointing down */}
          <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900"></div>
        </div>
      )}

    </div>
  );
};

export default DharmaSastramPage;
